import { useEffect, useState } from "react";

/**
 * Captures the `selectedPackageId` value present on the case at the moment the
 * arrangement context first becomes ready (e.g. when `config` is loaded). The
 * snapshot is taken once and never updated — user interactions that mutate
 * `selectedPackageId` afterwards do not move it. Use the returned id to mark
 * the case-originated plan with a "Pre-selected for you" badge, without
 * treating the family's first click as if it came from the case.
 *
 * If the case had no package attached at ready-time, the snapshot is `null`
 * forever; the caller's strict-equality check naturally renders no badge.
 */
export function useInitialSelectedPackageId(
  currentSelectedPackageId: string | null,
  isReady: boolean,
): string | null {
  const [snapshot, setSnapshot] = useState<{ value: string | null } | null>(
    null,
  );
  useEffect(() => {
    if (snapshot !== null) return;
    if (!isReady) return;
    setSnapshot({ value: currentSelectedPackageId });
  }, [isReady, currentSelectedPackageId, snapshot]);
  return snapshot?.value ?? null;
}
